import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { Platform } from 'react-native';
import type { PlanSet } from '@tabbio/types';
import {
  CheckoutController,
  isCheckoutBusy,
  type CheckoutBilling,
  type CheckoutSelection,
} from '../lib/subscription-journey/checkout-flow';
import { useRevenueCat } from './useRevenueCat';

export interface UseCheckoutFlowOptions {
  planSet: PlanSet;
  /** Employer plans only; ignored for individual purchases. */
  companyId?: string | null;
  /**
   * Fires once, after a synced snapshot confirms the tier (never on a tap).
   * Onboarding: `persistPlanAndAdvance(selection.tier)`. Paywall:
   * `router.replace('/subscription/welcome?tier=…&returnTo=…')`.
   */
  onPurchased?: (selection: CheckoutSelection) => void;
  /** Tests/stories only. Defaults to the app's RevenueCat provider. */
  billing?: CheckoutBilling;
}

/**
 * The checkout (C) state machine for one host screen (chooser or paywall).
 *
 *   const checkout = useCheckoutFlow({ planSet: 'individual', onPurchased });
 *   checkout.openReview({ tier: 'pro', cycle: 'yearly' }); // "Get Pro"
 *   checkout.purchase();    // "Continue to App Store" - one startPurchase per tap
 *   checkout.checkAgain();  // pending "Check again" - sync only
 *   checkout.restore();     // "Restore an existing purchase"
 *   checkout.backToPicker();// header "<" while on review
 *
 * `state.status` drives the UI: review | purchasing | restoring | checking |
 * redirecting (web left for hosted checkout) | purchased | pending | cancelled
 * | error | unchanged.
 */
export function useCheckoutFlow(options: UseCheckoutFlowOptions) {
  const revenueCat = useRevenueCat();
  const billing = options.billing ?? revenueCat;
  const surface = Platform.OS === 'web' ? 'web' : 'native';
  const controllerRef = useRef<CheckoutController | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = new CheckoutController({
      billing,
      planSet: options.planSet,
      companyId: options.companyId ?? null,
      surface,
      onPurchased: options.onPurchased,
    });
  }
  const controller = controllerRef.current;

  // Keep the latest callbacks/billing without recreating the machine (and
  // losing an in-flight purchase) on every render.
  useEffect(() => {
    controller.setOptions({
      billing,
      planSet: options.planSet,
      companyId: options.companyId ?? null,
      surface,
      onPurchased: options.onPurchased,
    });
  }, [
    billing,
    controller,
    options.companyId,
    options.onPurchased,
    options.planSet,
    surface,
  ]);

  const state = useSyncExternalStore(
    controller.subscribe,
    controller.getState,
    controller.getState,
  );

  return useMemo(
    () => ({
      state,
      isBusy: isCheckoutBusy(state),
      openReview: (selection: CheckoutSelection) =>
        controller.openReview(selection),
      backToPicker: () => controller.backToPicker(),
      purchase: () => controller.purchase(),
      restore: () => controller.restore(),
      checkAgain: () => controller.checkAgain(),
      reset: () => controller.reset(),
    }),
    [controller, state],
  );
}
